/**
 * OnboardingIdentity.jsx — "About you" (s95 sign-up rebuild)
 *
 * ★ s95: THIS IS NOW THE ONE IDENTITY SCREEN. The Welcome basics screen (name, DOB, sex,
 *   ZIP, email, mobile) is folded in here and retired: email and mobile were already asked
 *   at sign-up (and an edit to them here never reached the account), and ZIP was asked
 *   twice. The optional ID scan is offered FIRST and fills name, DOB and address; every
 *   typed field is required (Dr. Rami, 2026-10-04: "I just need the typed fields but
 *   prefer the ID"). 18+ is refused here as it was on Welcome.
 *
 * Original header, v213.7 (C-A-OB-FIX):
 *
 * Screen 3 of 7 (after welcome basics + auto-fill). ID front + back
 * capture. Basics (name, DOB, sex, ZIP, mobile, email) were already
 * captured on the welcome screen, so this screen is purely about the
 * ID document.
 *
 * If OCR returns name / DOB / sex / address fields, we auto-update the
 * existing identity values (filling in anything still blank) and show
 * a "from scan" pill in the patient's downstream Profile editor.
 */
import { useState, useRef, useEffect } from 'react'
import { Camera, Check, ChevronRight, MapPin, X } from 'lucide-react'
import { useI18n } from '../../i18n'
import { useOnboardingStore, useUserStore } from '../../stores'
import DobPicker from '../../components/DobPicker'
import { ageVerdict, ACCOUNT_HOLDER_MIN_AGE } from '../../utils/age'
import CardScanner from '../../components/CardScanner'
import useCardScanPair from '../../hooks/useCardScanPair'
import { processCardFile, persistCardData } from '../../utils/processCardImage'
import { cardUploadFailureMessage } from '../../utils/uploadRefusal'
// TEST 1.02 / PROD-176 — the ONE rule for what a scan may write, shared with the profile
// ID screen, and the ONE test for a refused card. See utils/idScanAutofill.js.
import { idScanFill, isIdScanRefused } from '../../utils/idScanAutofill'
// TEST 3.06: the ONE required-field list the profile's identification gate reads.
import { missingIdentificationFields } from '../../utils/profileCompletion'
import {
  OnboardingShell,
  ScreenHeader,
  ScreenIntro,
  PrimaryButton,
  TextField,
  SegmentedSelect,
  Footer,
  ScrollBody,
  useScrollToTop,
  NAVY,
  BLUE,
  LIGHT_BLUE,
  ICE_BLUE,
  GREEN,
  PASTEL_GREEN,
  TEXT_MUTED,
  TEXT_HINT,
} from './onboardingPrimitives'
import { OrellaSpinner } from '../../components/OrellaLoader' // s123: the app's one spinner

function CardSlot({ side, captured, onCamera, onFile, onRemove, label, busy }) {
  const { t } = useI18n()
  const inputRef = useRef(null)
  const [drag, setDrag] = useState(false)
  const pick = (e) => { e.stopPropagation(); inputRef.current?.click() }
  const onInput = (e) => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = '' }
  const onDrop = (e) => { e.preventDefault(); setDrag(false); const f = e.dataTransfer?.files?.[0]; if (f) onFile(f) }
  return (
    <div
      onClick={captured || busy ? undefined : onCamera}
      onDragOver={(e) => { e.preventDefault(); if (!captured && !busy) setDrag(true) }}
      onDragLeave={() => setDrag(false)}
      onDrop={captured || busy ? undefined : onDrop}
      className="flex flex-col items-center justify-center transition"
      style={{
        width: '100%',
        aspectRatio: '1.586 / 1',
        borderRadius: 14,
        background: captured ? PASTEL_GREEN + '40' : (drag ? '#DCEEFF' : ICE_BLUE),
        border: `2px dashed ${captured ? GREEN : (drag ? BLUE : LIGHT_BLUE)}`,
        cursor: captured || busy ? 'default' : 'pointer',
        position: 'relative',
        overflow: 'hidden',
        gap: 2,
      }}
    >
      {captured ? (
        <>
          {captured.url || captured.data ? (
            <img
              src={captured.url || captured.data}
              alt={label}
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : null}
          <div
            className="absolute"
            style={{ top: 8, left: 8, padding: '3px 8px', borderRadius: 999, background: GREEN, color: '#fff', fontSize: 11.5, fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 4 }}
          >
            <Check size={12} strokeWidth={3} />
            {label}
          </div>
          {onRemove && (
            <button
              type="button"
              aria-label={`Remove ${label}`}
              onClick={(e) => { e.stopPropagation(); onRemove() }}
              className="absolute"
              style={{ top: 6, right: 6, width: 28, height: 28, borderRadius: 999, border: 'none', background: 'rgba(21,53,79,0.78)', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 }}
            >
              <X size={16} strokeWidth={2.5} />
            </button>
          )}
        </>
      ) : busy ? (
        <>
          <OrellaSpinner size={22} />
          <span style={{ fontSize: 12.5, fontWeight: 500, color: NAVY, marginTop: 4 }}>{t('cardScanner.reading', 'Reading…')}</span>
        </>
      ) : (
        <>
          <Camera size={26} color={BLUE} />
          <span style={{ fontSize: 14, fontWeight: 600, color: NAVY, marginTop: 2 }}>{label}</span>
          <span style={{ fontSize: 11, color: TEXT_MUTED }}>{t('onboarding.takePhoto', 'Take photo')}</span>
          <input ref={inputRef} type="file" accept="image/*" onChange={onInput} style={{ display: 'none' }} />
        </>
      )}
    </div>
  )
}

export default function OnboardingIdentity({ onNext, onBack, onExit }) {
  const { t } = useI18n()
  const identity = useOnboardingStore((s) => s.identity)
  const setIdentity = useOnboardingStore((s) => s.setIdentity)

  const [scannerSide, setScannerSide] = useState(null) // null | 'idFront' | 'idBack'
  // v228.11.1: once the patient edits the ZIP by hand, a later re-scan must not
  // clobber it. (Decision B otherwise lets the scanned ZIP win over the Welcome ZIP.)
  const zipTouchedRef = useRef(false)
  // TEST 3.06: the address is no longer skippable. Registration asks for exactly what
  // Profile → Identification requires (utils/profileCompletion.js), so a finished
  // onboarding leaves that section complete. Same touched/allValid shape as
  // OnboardingWelcome.handleContinue.
  const [touched, setTouched] = useState(false)
  // s95: seed the name from the account (sign-up form, or what Apple/Google gave us).
  const account = useUserStore((s) => s.user || s.pendingProfile)
  useEffect(() => {
    if (!account) return
    const patch = {}
    if (account.firstName && !identity.firstName) patch.firstName = account.firstName
    if (account.lastName && !identity.lastName) patch.lastName = account.lastName
    if (Object.keys(patch).length) setIdentity(patch)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account])
  const dobVerdict = ageVerdict(identity.dob, ACCOUNT_HOLDER_MIN_AGE)
  const allValid = missingIdentificationFields(identity).length === 0 && dobVerdict === 'ok'
  const handleContinue = () => {
    setTouched(true)
    if (!allValid) return
    onNext()
  }

  useScrollToTop('identity')

  // PROD-1189: the front carries the OCR'd license fields and is the only side read; the
  // pair hook fires this as soon as a front with extracted fields arrives (the back is
  // stored, not read). Filling only blanks makes a repeat call harmless.
  // TEST 1.02 R2 — the server said it will not keep the ID just scanned (the name on it is
  // not this account's). Nothing else is blocked: an ID is optional and Continue stays on.
  const [idRefused, setIdRefused] = useState(false)

  const cardPair = useCardScanPair('ID', (merged) => {
    // ★ The one fill-blanks-only rule. v228.11.1's Decision B survives as the only
    //   exception: the card's ZIP wins so the address stays self-consistent, unless the
    //   patient has already edited it here. It can never reach a name.
    const updates = idScanFill(identity, merged, { zipFromCard: !zipTouchedRef.current })
    if (Object.keys(updates).length) setIdentity(updates)
  })

  // ★★ A REFUSED CARD IS NOT KEPT, AND BOTH SIDES GO WITH IT — the server's profile
  //   writers drop idFront AND idBack while the verdict on the row is 'mismatched', so
  //   showing a back as "captured" here would be promising something the save will undo.
  //   A later front that IS accepted clears the refusal.
  const refuseIfNeeded = (sideKey, cardData) => {
    const normSide = sideKey === 'idFront' ? 'idFront' : 'idBack'
    if (normSide === 'idFront' && isIdScanRefused(cardData)) {
      setIdRefused(true)
      setIdentity({ idFront: null, idBack: null })
      cardPair.clearSide('idFront')
      cardPair.clearSide('idBack')
      return true
    }
    if (normSide === 'idFront') setIdRefused(false)
    if (normSide === 'idBack' && idRefused) return true
    return false
  }

  const handleScanCapture = (cardData) => {
    if (!cardData) {
      setScannerSide(null)
      return
    }
    const side = scannerSide
    const sideKey = side === 'idFront' ? 'idFront' : 'idBack'
    if (refuseIfNeeded(sideKey, cardData)) { setScannerSide(null); return }
    // Store the image now so the slot shows "captured". PROD-1189: an ID front with
    // extracted fields auto-fills at once (via the pair hook); the back is only stored.
    const token = cardPair.beginSide(side) // PROD-1189b
    setIdentity({ [sideKey]: cardData })
    cardPair.captureSide(side, cardData, token)
    setScannerSide(null)
    // v228.11.7: persist the camera shot to storage in the background so it isn't
    // dropped on save (profile stores the reference, not base64). Merge when ready.
    persistCardData(cardData, 'ID', side === 'idFront' ? 'front' : 'back').then((enriched) => {
      if (!cardPair.isCurrent(token)) return // PROD-1189b: removed or replaced meanwhile
      if (enriched && enriched.storagePath) {
        setIdentity({ [sideKey]: enriched })
        cardPair.captureSide(side, enriched, token)
      } else if (enriched && enriched.uploadError) {
        // ★ P2 Release 3: the photo was NOT stored and will not survive the save. Say so.
        alert(cardUploadFailureMessage(t, enriched.uploadError))
      }
    })
  }

  // v228.11.0: upload / drag-drop path — same pipeline as the camera.
  const [uploadingSide, setUploadingSide] = useState(null)
  const uploadSeqRef = useRef(0) // PROD-1189b: the newest upload owns the spinner
  const handleSideFile = async (sideKey, file) => {
    setUploadingSide(sideKey)
    const seq = ++uploadSeqRef.current
    const token = cardPair.beginSide(sideKey) // PROD-1189b: taken BEFORE the extract starts
    try {
      const cardData = await processCardFile(file, 'ID', sideKey === 'idFront' ? 'front' : 'back')
      if (!cardPair.isCurrent(token)) return // PROD-1189b: cleared or replaced while reading: drop it
      if (refuseIfNeeded(sideKey, cardData)) return
      setIdentity({ [sideKey]: cardData })
      cardPair.captureSide(sideKey, cardData, token)
      if (cardData && cardData.uploadError) alert(cardUploadFailureMessage(t, cardData.uploadError))
    } catch (e) {
      if (!cardPair.isCurrent(token)) return // PROD-1189b: abandoned request: no alert
      alert(e?.message || 'Could not read that image — try another photo.')
    } finally {
      if (uploadSeqRef.current === seq) setUploadingSide(null) // only the newest upload ends the spinner
    }
  }

  // v228.11.3: let the patient remove a captured card. Clears that side's image
  // and the pair-hook entry; the other side and any already-verified address
  // fields are left intact (they can re-capture or continue without it).
  const handleRemoveSide = (sideKey) => {
    setIdentity({ [sideKey]: null })
    cardPair.clearSide(sideKey)
  }

  // ID is recommended but not blocking. Continue is always enabled — we
  // hint to the patient that they can add it later.
  const hasFront = !!identity.idFront
  const hasBack = !!identity.idBack

  return (
    <>
      <OnboardingShell>
        <ScreenHeader onBack={onBack} step={1} onExit={onExit} />
        <ScreenIntro
          eyebrow={t('onboarding.about.eyebrow', 'About you')}
          title={t('onboarding.about.title', "Let's start with the basics")}
          dialogue={t('onboarding.about.dialogue', 'Scan your ID to fill this in, or type your details below.')}
          required
        />

        <ScrollBody>
          <div className="grid grid-cols-2 gap-3" style={{ marginBottom: 14 }}>
            <CardSlot
              side="idFront"
              captured={identity.idFront}
              onCamera={() => setScannerSide('idFront')}
              onFile={(f) => handleSideFile('idFront', f)}
              onRemove={() => handleRemoveSide('idFront')}
              busy={uploadingSide === 'idFront'}
              label={t('onboarding.idupload.front', 'Front')}
            />
            <CardSlot
              side="idBack"
              captured={identity.idBack}
              onCamera={() => setScannerSide('idBack')}
              onFile={(f) => handleSideFile('idBack', f)}
              onRemove={() => handleRemoveSide('idBack')}
              busy={uploadingSide === 'idBack'}
              label={t('onboarding.idupload.back', 'Back')}
            />
          </div>

          {/* TEST 1.02 R2 — the refusal, and the way out. Same keys as the profile ID
              screen, so the patient reads one sentence wherever they meet it. */}
          {idRefused && (
            <div className="rounded-[14px]" style={{ padding: '11px 13px', background: 'rgba(255,249,235,0.95)', border: '1.5px solid rgba(214,158,46,0.4)', marginBottom: 14 }}>
              <p style={{ fontSize: 12.5, fontWeight: 700, color: '#7A5A10' }}>{t('profileId.idNameMismatchTitle')}</p>
              <p style={{ fontSize: 11.5, marginTop: 4, lineHeight: 1.4, color: '#7A5A10' }}>{t('profileId.idNameMismatchBody')}</p>
            </div>
          )}

          {/* v228.11.0: one side captured. PROD-1189: shown only while the FRONT is in and the back
              is not (the front already filled; ask for the back so both sides are on file). With only
              the back captured nothing was read, so no hint. */}
          {(hasFront && !hasBack) && (
            <div
              className="flex items-center gap-2 rounded-[14px]"
              style={{ padding: '11px 13px', background: ICE_BLUE, border: `1.5px solid ${LIGHT_BLUE}`, marginBottom: 14 }}
            >
              <OrellaSpinner size={16} />
              <span style={{ fontSize: 12.5, fontWeight: 600, color: NAVY }}>
                {t('onboarding.idupload.addOther', 'Front read — add the back of your ID too so we keep both sides on file.')}
              </span>
            </div>
          )}

          {/* v228.11.1: address comes back as discrete editable fields the patient
              verifies before continuing. These map 1:1 to the profile identification
              keys (streetAddress / apt / city / state / zipCode), so they persist on
              save and resume. TEST 3.06: street, city, state and ZIP are REQUIRED here
              now (the profile requires them; the step used to be skippable). */}
          {/* TF-05 (v242.61.0) — "Address line not complete". This block used to be
              gated on `hasFront && hasBack`, so a patient who skipped the ID upload
              was NEVER SHOWN the address fields at all. Their profile then read
              complete on the home screen (which checked three fields) while the
              identification form refused to save without street/city/state — the
              report, exactly. The scan still PREFILLS these; it no longer decides
              whether the patient is allowed to be asked. */}
          <div className="grid grid-cols-2 gap-2">
            <TextField
              label={t('onboarding.about.firstName', 'Legal first name')}
              value={identity.firstName}
              onChange={(v) => setIdentity({ firstName: v })}
              required
              autoComplete="given-name"
            />
            <TextField
              label={t('onboarding.welcome.lastName', 'Last name')}
              value={identity.lastName}
              onChange={(v) => setIdentity({ lastName: v })}
              required
              autoComplete="family-name"
            />
          </div>
          <DobPicker
            label={t('onboarding.welcome.dob', 'Date of birth')}
            value={identity.dob}
            onChange={(v) => setIdentity({ dob: v })}
            minAge={18}
          />
          {dobVerdict === 'minor' && (
            <p role="alert" style={{ fontSize: 12.5, color: '#DC2626', marginTop: 4 }}>
              {t('dob.tooYoung', { defaultValue: 'You must be {{age}} or older to use Orella.', age: ACCOUNT_HOLDER_MIN_AGE })}
            </p>
          )}
          <div style={{ height: 10 }} />
          <SegmentedSelect
            label={t('onboarding.welcome.sex', 'Sex assigned at birth')}
            value={identity.sex}
            onChange={(v) => setIdentity({ sex: v })}
            options={[
              { value: 'female', label: t('onboarding.welcome.female', 'Female') },
              { value: 'male',   label: t('onboarding.welcome.male',   'Male')   },
              { value: 'other',  label: t('onboarding.welcome.other',  'Other')  },
            ]}
          />
          <div style={{ height: 14 }} />

          {true && (
            <div style={{ marginBottom: 8 }}>
              <div className="flex items-center gap-2" style={{ marginBottom: 12 }}>
                <MapPin size={17} color={BLUE} style={{ flexShrink: 0 }} />
                <span style={{ fontSize: 13, color: NAVY, fontWeight: 600 }}>
                  {t('onboarding.idupload.addressHeading', 'Your address')}
                </span>
                {(hasFront || hasBack) && (
                  <span style={{ fontSize: 10, color: BLUE, background: ICE_BLUE, padding: '1px 7px', borderRadius: 999, fontWeight: 500 }}>
                    {t('onboarding.idupload.fromScan', 'from your ID')}
                  </span>
                )}
              </div>

              <TextField
                label={t('onboarding.idupload.street', 'Street address')}
                value={identity.streetAddress}
                onChange={(v) => setIdentity({ streetAddress: v })}
                placeholder="123 Main St"
                required
                autoComplete="address-line1"
              />
              <TextField
                label={t('onboarding.idupload.apt', 'Apt / unit (optional)')}
                value={identity.apt}
                onChange={(v) => setIdentity({ apt: v })}
                placeholder=""
                required={false}
                autoComplete="address-line2"
              />
              <TextField
                label={t('onboarding.idupload.city', 'City')}
                value={identity.city}
                onChange={(v) => setIdentity({ city: v })}
                placeholder=""
                required
                autoComplete="address-level2"
              />
              <div className="flex gap-3">
                <div style={{ flex: 1 }}>
                  <TextField
                    label={t('onboarding.idupload.state', 'State')}
                    value={identity.state}
                    onChange={(v) => setIdentity({ state: v })}
                    placeholder="CA"
                    required
                    maxLength={20}
                    autoComplete="address-level1"
                  />
                </div>
                <div style={{ flex: 1.3 }}>
                  <TextField
                    label={t('onboarding.idupload.zip', 'ZIP')}
                    value={identity.zipCode}
                    onChange={(v) => { zipTouchedRef.current = true; setIdentity({ zipCode: v }) }}
                    placeholder="92103"
                    required
                    inputMode="numeric"
                    maxLength={10}
                    autoComplete="postal-code"
                  />
                </div>
              </div>
            </div>
          )}

          {touched && !allValid && (
            <p style={{ fontSize: 12.5, color: '#DC2626', marginBottom: 8 }}>
              {t('onboarding.welcome.missingFields', 'Please fill in all required fields.')}
            </p>
          )}

          <p style={{ fontSize: 13, color: TEXT_HINT, lineHeight: 1.5 }}>
            {t('onboarding.about.note', 'The ID photo is optional. It speeds up check-in at your visit, and you can add it later from your profile.')}
          </p>
        </ScrollBody>

        <Footer>
          <PrimaryButton onClick={handleContinue} disabled={!allValid}>
            {hasFront && hasBack
              ? t('common.continue', 'Continue')
              : (hasFront || hasBack
                ? t('onboarding.idupload.continueOne', 'Continue with what I have')
                : t('common.continue', 'Continue'))}
          </PrimaryButton>
          {/* TEST 3.06: "I'll add this later" is gone. It skipped the address the profile
              then demanded; the ID photos stay optional through Continue. */}
        </Footer>
      </OnboardingShell>

      {scannerSide && (
        <CardScanner
          cardType="ID"
          side={scannerSide === 'idFront' ? 'front' : 'back'}
          onCapture={handleScanCapture}
          onCancel={() => setScannerSide(null)}
          onUpload={(file) => { const s = scannerSide; setScannerSide(null); handleSideFile(s, file) }}
          enableOCR={true}
        />
      )}
    </>
  )
}
